/** Auth API calls + mapping the JWT payload to the frontend CurrentUser shape. */

import { apiGet, apiPost } from "@/lib/api-client";
import type { CurrentUser, RoleName } from "@/lib/permissions";

/** Shape returned by `/auth/login`.user and `/auth/me` (the JWT payload). */
export interface AuthUserPayload {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  /** Spec §3 — refus explicites, absents des jetons émis avant cette version. */
  deniedPermissions?: string[];
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUserPayload;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

/** Map the API JWT payload (`sub`) to the UI CurrentUser (`id`). */
export function toCurrentUser(payload: AuthUserPayload): CurrentUser {
  return {
    id: payload.sub,
    name: payload.name,
    email: payload.email,
    roles: payload.roles as RoleName[],
    permissions: payload.permissions,
    // Spec §3 — sans cette clé, l'objet littéral supprimait les refus explicites
    // au runtime et `can()` ne pouvait jamais les voir.
    deniedPermissions: payload.deniedPermissions ?? [],
  };
}

export const authApi = {
  login: (email: string, password: string) => apiPost<LoginResponse>("/auth/login", { email, password }),
  me: () => apiGet<AuthUserPayload>("/auth/me"),
  refresh: (refreshToken: string) => apiPost<RefreshResponse>("/auth/refresh", { refreshToken }),
};
